import { useCallback, useRef, useState } from "react";
import { PrefProduct, PrefStockList, PrefStockParams, SupplierSearchApiParams, SupplierStock, SupplierStockSingle, SupplierWarehouse, SupplierWarehouseAmount, SupplierWarehouseOptions } from "../types";
import supplierStockListApi from "../api/supplierStockListApi";
import { Severity } from "../../../type";
import getPrefStockListApi from "../api/getPrefStockListApi";
import getSupplierWarehouseApi from "../api/getSupplierWarehouseApi";
import { removeEmptyValues } from "../utils/common";

const limit = 10;

/**
 * 仕入先・外部倉庫在庫一覧取得
 * @param showAlert メッセージ表示アラート
 */
export default function useSupplierStockList(
  showAlert: (msg: string, severity?: Severity) => void,
) {
  const [total, setTotal] = useState(0); // 在庫のレコードの総数
  const [stockList, setStockList] = useState<SupplierStock[]>([]);
  const [backupStockList, setBackupStockList] = useState<SupplierStock[]>([]); // 変更したカラムのみ送信するため、元の取得したデータを保持しておく。
  const [changedStockList, setChangedStockList] = useState<SupplierStockSingle[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isMoreLoading, setIsMoreLoading] = useState(false); // 更に読み込む機能のLoading
  const [offset, setOffset] = useState(0);
  const isFastLoading = useRef(false); // 二重読み込み防止
  const [fireMoreLoadEvent, setFireMoreLoadEvent] = useState(false); // スクロール最下層に達した時、値を変更して発火させるイベント
  const [init, setInit] = useState(false);
  // 都道府県ごとの在庫
  const [isPrefLoading, setIsPrefLoading] = useState(false);
  const [prefStockList, setPrefStockList] = useState<PrefStockList>([]);
  const [centerWarehouseOptions, setCenterWarehouseOptions] = useState<SupplierWarehouse[]>([]);
  const [regionWarehouseOptions, setRegionWarehouseOptions] = useState<SupplierWarehouse[]>([]);
  const nullSupplierWarehouse = {code: "", name: "", isCenter: true}; // 仕入先倉庫でdepot_cd,depot_name,depot_kindがnullでquantityがnumberのレコードの仕入先倉庫選択肢

  // 仕入先倉庫の取得
  const getSupplierWarehouse = async (vendor_cd?: string) => {
    console.log('仕入先倉庫取得 vendor_cd：', vendor_cd);
    const cWarehouses = await getSupplierWarehouseApi(removeEmptyValues({
      vendor_cd,
      depot_kind: 0,
    }));
    const rWarehouses = await getSupplierWarehouseApi(removeEmptyValues({
      vendor_cd,
      depot_kind: 1,
    }));
    // setCenterWarehouseOptions([nullSupplierWarehouse, ...cWarehouses]);
    setCenterWarehouseOptions(cWarehouses);
    setRegionWarehouseOptions(rWarehouses);
    return {
      centerWarehouseOptions: cWarehouses,
      regionWarehouseOptions: rWarehouses,
    }
  };
  // 仕入先の場合、選択肢にない、足りないsupplierWarehousesをfillする。
  const fillWarehouse = (ops: SupplierWarehouse[], sList: SupplierStock[], isCenter: boolean) => {
    const newSList: SupplierStock[] = sList;
    sList.forEach((s, index) => {
      let filledArray: SupplierWarehouseAmount[] = []; // isCenterが一致する、今回fill対象の仕入先倉庫
      let leftArray: SupplierWarehouseAmount[] = []; // isCenterが一致しない、今回fill対象ではない仕入先倉庫
      if (s.supplierWarehouses && s.supplierWarehouses.length > 0) {
        leftArray = s.supplierWarehouses!.filter(sw => isCenter !== sw.isCenter);
        ops.forEach((wo) => {
          const found = s.supplierWarehouses!.find(sw => wo.code === sw.code);
          if (found) {
            filledArray.push(found);
          } else {
            filledArray.push({
              code: wo.code,
              name: wo.name,
              amount: 0,
              isCenter: wo.isCenter,
            })
          }
        });
      } else {
        filledArray = ops.map(wo => ({
          code: wo.code,
          name: wo.name,
          amount: 0,
          isCenter: wo.isCenter,
        }));
      }
      // fillしたものを代入
      newSList[index].supplierWarehouses = [...filledArray, ...leftArray];
    });
    console.log('fillWarehouse:', isCenter, newSList, sList);
    // fillしたものを返す
    return newSList;
  };

  const get = async (searchParams: SupplierSearchApiParams, isSupplier: boolean, vendor_cd?: string) => {
    isFastLoading.current = true;
    setIsLoading(true);
    try {
      const res = await supplierStockListApi({ ...searchParams, ...{ offset: 0, limit } });
      let sList = res.stocks;

      // 仕入先の場合はfillする
      if (isSupplier) {
        let opt: SupplierWarehouseOptions = {
          centerWarehouseOptions: [],
          regionWarehouseOptions: []
        };
        if (vendor_cd) opt = await getSupplierWarehouse(vendor_cd);
        else opt = await getSupplierWarehouse();
        sList = fillWarehouse(opt.centerWarehouseOptions, sList, true);
        sList = fillWarehouse(opt.regionWarehouseOptions, sList, false);
      }

      setTotal(res.total);
      setStockList(structuredClone(sList));
      setBackupStockList(structuredClone(sList));
      setOffset(sList.length);
      console.log('stockList backup before:', stockList, backupStockList);
      setTimeout(() => {console.log('stockList backup after:', stockList, backupStockList)}, 0);
    } catch (err) {
      console.error(err);
      showAlert('在庫一覧情報の取得に失敗しました。', 'error');
    } finally {
      isFastLoading.current = false;
      setIsLoading(false);
    }
  };

  // 無限スクロール時の読み込み
  const getMore = async (searchParams: SupplierSearchApiParams, isSupplier: boolean, vendor_cd?: string) => {
    if (stockList.length >= total || !init) {
      return;
    }

    isFastLoading.current = true;
    setIsMoreLoading(true);
    try {
      const res = await supplierStockListApi({
        limit,
        offset,
        ...searchParams,
      });
      let sList = res.stocks;

      // 仕入先の場合はfillする
      if (isSupplier) {
        // 既に取得しているのでそちらを使う
        sList = fillWarehouse(centerWarehouseOptions, sList, true);
        sList = fillWarehouse(regionWarehouseOptions, sList, false);
      }

      setTotal(res.total);
      setOffset((prev) => prev + sList.length);
      setStockList((prev) => [...prev, ...structuredClone(sList)]);
      setBackupStockList((prev) => [...prev, ...structuredClone(sList)]);
      console.log("stockList backup before more:", stockList, backupStockList);
      setTimeout(() => {
        console.log("stockList backup after more:", stockList, backupStockList);
      }, 0);
    } catch (err) {
      console.error(err);
      showAlert("在庫一覧情報の取得に失敗しました。", "error");
    } finally {
      isFastLoading.current = false;
      setIsMoreLoading(false);
    }
  };

  // 値が変わったレコードのみ抽出
  const updatedStockList = useCallback(() => {
    const cList = stockList.filter((s) => {
      const bbs = backupStockList.filter((bs) => bs.id === s.id)[0];
      return !(
        // bbs.amountSymbol === s.amountSymbol &&
        (
          bbs.amountCount === s.amountCount &&
          bbs.arrivalDate === s.arrivalDate &&
          (bbs.supplierWarehouses && bbs.supplierWarehouses.length > 0
            ? bbs.supplierWarehouses.reduce(
                (acc, cur, index) => acc && cur.amount === s.supplierWarehouses![index].amount,
                true,
              )
            : true)
        )
        // && bbs.stockVisibleType === s.stockVisibleType &&
        // bbs.isDiscontinued === s.isDiscontinued
      );
    }) as SupplierStockSingle[];
    setChangedStockList(cList);
    return cList;
  }, [stockList, backupStockList]);
  const resetAmount = () => setStockList(structuredClone(backupStockList));

  // 都道府県毎に取得
  const getPrefStock = async (prefProduct: PrefProduct) => {
    const params: PrefStockParams = {
      vendor_cd: prefProduct.supplierCd,
      item_cd: prefProduct.productCd,
      depot_kind: Number(!prefProduct.isCenter) as 0 | 1,
    };
    setIsPrefLoading(true);
    try {
      const res = await getPrefStockListApi(params);
      setPrefStockList(res);
    } catch (err) {
      console.error(err);
      showAlert('都道府県ごとの在庫取得に失敗しました。');
    } finally {
      setIsPrefLoading(false);
    }

  };

  return [
    {
      total,
      stockList,
      isLoading,
      isMoreLoading,
      changedStockList,
      isFastLoading,
      fireMoreLoadEvent,
      isPrefLoading,
      prefStockList,
    },
    {
      setTotal,
      setStockList,
      get,
      getMore,
      updatedStockList,
      setFireMoreLoadEvent,
      setInit,
      resetAmount,
      setChangedStockList,
      setBackupStockList,
      getPrefStock,
    },
  ] as const;
}
